/* WhOnboardingGate (iter448) — condition-first onboarding для Women's Health.
 *
 * Flow:
 *   1. Fetch /api/wh/onboarding/state.
 *   2. If no active program → render <ConditionSelector />.
 *   3. If active program === "pmos-reset" but no PCOS assessment → render <PcosRotterdamForm />.
 *   4. Otherwise render children (the actual v3 tabs).
 *
 * Bulgarian-only labels per user requirement. No anglicisms.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2, ChevronRight, CheckCircle2, Sparkles, Heart, Activity, Flame, Sun, FileUp,
} from "lucide-react";
import { formatApiError } from "@/lib/formatApiError";

const CATEGORY_ICON = {
  "hormonal-reproductive": Heart,
  "metabolic": Activity,
  "pelvic-pain": Flame,
  "life-stage": Sun,
};

const LAB_LABEL = {
  total_testosterone_nmol_l: "Общ тестостерон (nmol/L)",
  free_testosterone_pmol_l: "Свободен тестостерон (pmol/L)",
  shbg_nmol_l: "SHBG (nmol/L)",
  lh_iu_l: "LH (IU/L)",
  fsh_iu_l: "FSH (IU/L)",
  prolactin_mu_l: "Пролактин (mU/L)",
  tsh_mu_l: "TSH (mU/L)",
  amh_ng_ml: "AMH (ng/mL)",
  dheas_umol_l: "DHEA-S (μmol/L)",
  androstenedione_nmol_l: "Андростендион (nmol/L)",
  estradiol_pmol_l: "Естрадиол (pmol/L)",
  progesterone_nmol_l: "Прогестерон (nmol/L)",
  fasting_glucose_mmol_l: "Кр. захар на гладно (mmol/L)",
  fasting_insulin_mu_l: "Инсулин на гладно (mU/L)",
  hba1c_percent: "HbA1c (%)",
  homa_ir: "HOMA-IR",
  hs_crp_mg_l: "hs-CRP (mg/L)",
  vitamin_d_ng_ml: "Витамин D (ng/mL)",
};

export default function WhOnboardingGate({ API, isDark, children }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/wh/onboarding/state`);
      setState(data);
    } catch (e) {
      toast.error("Грешка при зареждане на онбординг данните");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="wh-onboarding-loading">
        <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
      </div>
    );
  }

  // Step 1 — no active program: must pick a condition first
  if (!state?.has_active_program) {
    return (
      <ConditionSelector
        API={API}
        isDark={isDark}
        submitting={submitting}
        setSubmitting={setSubmitting}
        onSelected={refresh}
      />
    );
  }

  // PCOS assessment is NOT a hard gate — user can fill it any time via "Моят път".
  // The app is fully accessible after picking a condition.
  return children;
}

/* ─────────────────────── Condition Selector ─────────────────────── */
function ConditionSelector({ API, isDark, submitting, setSubmitting, onSelected }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/wh/conditions`);
        setCategories(data.categories || []);
      } catch { /* */ }
      finally { setLoading(false); }
    })();
  }, [API]);

  const choose = async (condition) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/wh/v3/programs/${condition.program_slug}/activate`);
      toast.success(`Активирана програма: ${condition.label_bg}`);
      onSelected();
    } catch (e) {
      toast.error("Неуспешно активиране на програма");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto my-12" />;
  }

  return (
    <div data-testid="wh-onboarding-condition-selector">
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-5 sm:p-7">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="wh-heading text-xl sm:text-2xl">Изберете състояние</h2>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                Платформата ще ви предложи поведенческа програма според вашия избор.
              </p>
            </div>
          </div>
          <p className={`text-[11px] mb-4 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
            Това е образователен инструмент, не медицинска диагноза. Винаги се консултирайте с лекар.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICON[cat.key] || Heart;
              return (
                <div
                  key={cat.key}
                  className={`rounded-2xl border p-4 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-[#FBF8F1] border-[#E5E2DA]"}`}
                  data-testid={`wh-onboarding-cat-${cat.key}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
                    <h3 className="font-bold text-sm">{cat.label_bg}</h3>
                  </div>
                  <p className={`text-[11px] mb-3 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                    {cat.description_bg}
                  </p>
                  <div className="space-y-2">
                    {cat.conditions.map((c) => (
                      <button
                        key={c.slug}
                        type="button"
                        disabled={submitting}
                        onClick={() => choose(c)}
                        className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition ${
                          isDark
                            ? "bg-slate-950 border-slate-800 hover:border-pink-500 text-slate-100"
                            : "bg-white border-[#E5E2DA] hover:border-pink-400 text-[#2A342B]"
                        }`}
                        data-testid={`wh-onboarding-condition-${c.slug}`}
                      >
                        <span className="text-sm font-medium">{c.label_bg}</span>
                        <span className="flex items-center gap-1">
                          {c.has_assessment && (
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isDark ? "bg-violet-950/40 text-violet-300" : "bg-violet-50 text-violet-700"}`}>
                              + оценка
                            </span>
                          )}
                          <ChevronRight className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-[#8F9990]"}`} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────────────── PCOS Rotterdam Form ─────────────────────── */
export function PcosRotterdamForm({ API, isDark, submitting, setSubmitting, onDone, onSkip }) {
  const [form, setForm] = useState({
    cycle_irregularity: "unknown",
    avg_cycle_length_days: "",
    n_cycles_per_year: "",
    hirsutism: false,
    acne_persistent: false,
    androgenic_alopecia: false,
    free_testosterone_high: null,
    shbg_low: null,
    pcom_reported_by_us: null,
    bmi_kg_m2: "",
    family_history_pcos: false,
    family_history_t2dm: false,
  });
  const [result, setResult] = useState(null);
  // iter453: AI lab parsing inline.
  const [labParsing, setLabParsing] = useState(false);
  const [labResult, setLabResult] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleLabUpload = async (file) => {
    if (!file || labParsing) return;
    setLabParsing(true);
    setLabResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("label", "PCOS — хормонален / метаболитен панел");
      const { data: up } = await axios.post(`${API}/wh/lab-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Качено. Анализирам с AI…");
      const { data: parsed } = await axios.post(`${API}/wh/lab-upload/${up.id}/parse`);
      const flags = parsed.rotterdam_flags || {};
      // Auto-fill the assessment form with derived flags
      setForm((f) => ({
        ...f,
        free_testosterone_high: flags.free_testosterone_high ?? flags.total_testosterone_high ?? f.free_testosterone_high,
        shbg_low: flags.shbg_low ?? f.shbg_low,
      }));
      setLabResult(parsed);
      toast.success("AI попълни лабораторните полета — проверете и потвърдете.");
    } catch (e) {
      toast.error(formatApiError(e, "Грешка при анализа на изследването"));
    } finally {
      setLabParsing(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        avg_cycle_length_days: form.avg_cycle_length_days ? parseInt(form.avg_cycle_length_days, 10) : null,
        n_cycles_per_year: form.n_cycles_per_year !== "" ? parseInt(form.n_cycles_per_year, 10) : null,
        bmi_kg_m2: form.bmi_kg_m2 ? parseFloat(form.bmi_kg_m2) : null,
      };
      const { data } = await axios.post(`${API}/wh/pcos/assessment`, payload);
      setResult(data);
      toast.success("Оценката е запазена");
    } catch (e) {
      toast.error(formatApiError(e, "Грешка при изпращане на оценката"));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-onboarding-pcos-result">
        <CardContent className="p-6 sm:p-7">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                Резултат от Rotterdam оценка
              </p>
              <h2 className="wh-heading text-xl">{result.phenotype_label_bg}</h2>
            </div>
          </div>

          <div className={`rounded-xl p-3 mb-3 text-sm ${isDark ? "bg-slate-900/60 text-slate-200" : "bg-[#FBF8F1] text-[#2A342B]"}`}>
            {result.interpretation_bg}
          </div>

          {result.meets_criteria && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className={`rounded-xl p-2.5 ${isDark ? "bg-rose-950/40" : "bg-rose-50"}`}>
                <p className="text-[10px] uppercase tracking-wider opacity-70">Метаболитен риск</p>
                <p className="text-sm font-bold">{result.metabolic_risk_bg}</p>
              </div>
              <div className={`rounded-xl p-2.5 ${isDark ? "bg-amber-950/40" : "bg-amber-50"}`}>
                <p className="text-[10px] uppercase tracking-wider opacity-70">Репродуктивен риск</p>
                <p className="text-sm font-bold">{result.reproductive_risk_bg}</p>
              </div>
            </div>
          )}

          {result.next_steps_bg?.length > 0 && (
            <div className="mb-3">
              <p className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                Препоръчителни следващи стъпки
              </p>
              <ul className="space-y-1">
                {result.next_steps_bg.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className={isDark ? "text-emerald-400" : "text-emerald-600"}>•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={`text-[10px] mb-4 ${isDark ? "text-slate-500" : "text-[#8F9990]"}`}>
            Източници: PubMed PMID {result.pubmed_refs?.join(", ")}
          </div>

          <Button
            onClick={onDone}
            className="bg-pink-500 hover:bg-pink-600 text-white w-full"
            data-testid="wh-onboarding-pcos-continue"
          >
            Продължи към програмата
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-onboarding-pcos-form">
      <CardContent className="p-5 sm:p-7">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
            <Heart className="w-5 h-5" />
          </div>
          <div>
            <h2 className="wh-heading text-xl sm:text-2xl">PCOS оценка (Rotterdam)</h2>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              Попълнете симптомите, които имате. Лабораторните стойности са по желание.
            </p>
          </div>
        </div>
        <p className={`text-[11px] mb-5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
          Това е образователен инструмент. Точна диагноза се поставя само от лекар чрез ехография и хормонален панел.
        </p>

        <div className="space-y-5">
          {/* iter453: AI lab upload — auto-fills hormone fields below */}
          <div className={`p-3 rounded-2xl border-2 border-dashed ${isDark ? "border-violet-700 bg-violet-950/20" : "border-violet-300 bg-violet-50/50"}`} data-testid="pcos-lab-upload-card">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white flex-shrink-0">
                <FileUp className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-bold ${isDark ? "text-violet-100" : "text-violet-900"}`}>
                  Качи своите изследвания (AI ще попълни полетата вместо теб)
                </p>
                <p className={`text-[11px] mb-2 ${isDark ? "text-violet-200/70" : "text-violet-700"}`}>
                  PNG или JPEG снимка на хормонален / метаболитен панел. AI извлича стойностите и предлага попълване.
                </p>
                <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition ${
                  labParsing
                    ? (isDark ? "bg-slate-800 text-slate-400" : "bg-slate-200 text-slate-500")
                    : "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white hover:shadow"
                }`} data-testid="pcos-lab-upload-input-label">
                  {labParsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                  {labParsing ? "AI анализира…" : "Избери снимка на изследване"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    disabled={labParsing}
                    onChange={(e) => handleLabUpload(e.target.files?.[0])}
                    data-testid="pcos-lab-upload-input"
                  />
                </label>
                {labResult && (
                  <div className={`mt-2 p-2 rounded-lg text-[11px] ${isDark ? "bg-emerald-950/30 text-emerald-200" : "bg-emerald-50 text-emerald-900"}`} data-testid="pcos-lab-result">
                    <p className="font-bold mb-1">✓ AI извлече стойности:</p>
                    <ul className="space-y-0.5">
                      {Object.entries(labResult.extracted || {}).filter(([k, v]) => v !== null && k !== "test_date_iso").slice(0, 6).map(([k, v]) => (
                        <li key={k} className="opacity-90">• {LAB_LABEL[k] || k}: <b>{v}</b></li>
                      ))}
                    </ul>
                    <p className="mt-1 italic opacity-75">{labResult.disclaimer_bg}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <SectionTitle isDark={isDark}>1. Менструален цикъл</SectionTitle>
          <RadioRow
            label="Цикъл"
            value={form.cycle_irregularity}
            options={[
              { v: "regular", l: "Редовен (26–32 дни)" },
              { v: "irregular", l: "Нередовен" },
              { v: "absent", l: "Липсва" },
              { v: "unknown", l: "Не знам" },
            ]}
            onChange={(v) => update("cycle_irregularity", v)}
            isDark={isDark}
            testid="cycle-irregularity"
          />
          <div className="grid grid-cols-2 gap-3">
            <NumberInput label="Средна дължина (дни)" value={form.avg_cycle_length_days} onChange={(v) => update("avg_cycle_length_days", v)} isDark={isDark} testid="avg-cycle-length" />
            <NumberInput label="Цикъла за година" value={form.n_cycles_per_year} onChange={(v) => update("n_cycles_per_year", v)} isDark={isDark} testid="n-cycles-per-year" />
          </div>

          <SectionTitle isDark={isDark}>2. Признаци на повишени андрогени</SectionTitle>
          <CheckboxRow label="Хирзутизъм (нежелано окосмение по лице/тяло)" value={form.hirsutism} onChange={(v) => update("hirsutism", v)} isDark={isDark} testid="hirsutism" />
          <CheckboxRow label="Упорито акне (възрастно, не реагира на лечение)" value={form.acne_persistent} onChange={(v) => update("acne_persistent", v)} isDark={isDark} testid="acne" />
          <CheckboxRow label="Косопад (мъжки тип)" value={form.androgenic_alopecia} onChange={(v) => update("androgenic_alopecia", v)} isDark={isDark} testid="alopecia" />

          <SectionTitle isDark={isDark}>3. Лаборатория (по желание)</SectionTitle>
          <TriToggle label="Висок свободен тестостерон" value={form.free_testosterone_high} onChange={(v) => update("free_testosterone_high", v)} isDark={isDark} testid="ft-high" />
          <TriToggle label="Нисък SHBG" value={form.shbg_low} onChange={(v) => update("shbg_low", v)} isDark={isDark} testid="shbg-low" />

          <SectionTitle isDark={isDark}>4. Ехографски находки (по желание)</SectionTitle>
          <TriToggle label="Поликистозна морфология (PCOM) от лекар" value={form.pcom_reported_by_us} onChange={(v) => update("pcom_reported_by_us", v)} isDark={isDark} testid="pcom" />

          <SectionTitle isDark={isDark}>5. Допълнителни рискови фактори</SectionTitle>
          <NumberInput label="BMI (kg/m²)" value={form.bmi_kg_m2} onChange={(v) => update("bmi_kg_m2", v)} step="0.1" isDark={isDark} testid="bmi" />
          <CheckboxRow label="Фамилна история на PCOS" value={form.family_history_pcos} onChange={(v) => update("family_history_pcos", v)} isDark={isDark} testid="fh-pcos" />
          <CheckboxRow label="Фамилна история на тип-2 диабет" value={form.family_history_t2dm} onChange={(v) => update("family_history_t2dm", v)} isDark={isDark} testid="fh-t2dm" />
        </div>

        <div className="flex items-center gap-2 mt-6">
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-pink-500 hover:bg-pink-600 text-white flex-1"
            data-testid="wh-onboarding-pcos-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Изпрати оценката
          </Button>
          <Button
            onClick={onSkip}
            variant="outline"
            disabled={submitting}
            data-testid="wh-onboarding-pcos-skip"
          >
            Пропусни засега
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* Helpers */
function SectionTitle({ children, isDark }) {
  return (
    <p className={`text-[10px] uppercase tracking-wider font-bold mt-2 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
      {children}
    </p>
  );
}

function RadioRow({ label, value, options, onChange, isDark, testid }) {
  return (
    <div>
      <p className={`text-xs mb-1.5 ${isDark ? "text-slate-300" : "text-[#2A342B]"}`}>{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              className={`px-2.5 py-1 rounded-full text-xs border ${
                active
                  ? "bg-pink-500 text-white border-pink-500"
                  : isDark ? "bg-slate-900 border-slate-700 text-slate-300" : "bg-white border-[#E5E2DA] text-[#2A342B]"
              }`}
              data-testid={`pcos-${testid}-${o.v}`}
            >
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckboxRow({ label, value, onChange, isDark, testid }) {
  return (
    <label className={`flex items-center gap-2 text-sm cursor-pointer ${isDark ? "text-slate-200" : "text-[#2A342B]"}`} data-testid={`pcos-${testid}-label`}>
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-pink-500"
        data-testid={`pcos-${testid}-input`}
      />
      <span>{label}</span>
    </label>
  );
}

function TriToggle({ label, value, onChange, isDark, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-sm ${isDark ? "text-slate-200" : "text-[#2A342B]"}`}>{label}</span>
      <div className="flex gap-1">
        {[
          { v: true, l: "Да" },
          { v: false, l: "Не" },
          { v: null, l: "Не знам" },
        ].map((o) => {
          const active = value === o.v;
          return (
            <button
              key={String(o.v)}
              type="button"
              onClick={() => onChange(o.v)}
              className={`px-2 py-1 rounded-md text-xs border ${
                active
                  ? "bg-pink-500 text-white border-pink-500"
                  : isDark ? "bg-slate-900 border-slate-700 text-slate-300" : "bg-white border-[#E5E2DA] text-[#5C665D]"
              }`}
              data-testid={`pcos-${testid}-${o.v === null ? "unknown" : (o.v ? "yes" : "no")}`}
            >
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, step = "1", isDark, testid }) {
  return (
    <label className="block">
      <span className={`text-[11px] block mb-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 rounded-xl border text-sm ${
          isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-[#E5E2DA] text-[#2A342B]"
        }`}
        data-testid={`pcos-${testid}-input`}
      />
    </label>
  );
}
